/**
 * InvitationsSummary — small admin-only dashboard widget that shows
 * trial-conversion-friendly numbers:
 *   • Pending invites
 *   • Expiring in ≤2 days
 *   • Accepted in the last 7 days
 *
 * Pulls directly from the tenant-scoped ``GET /api/invitations?include_all=true``
 * endpoint; aggregation is done on the client to avoid an extra round
 * trip. The widget only renders for admins because the invitations
 * routes themselves are admin-gated — a non-admin would see an empty
 * card anyway.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { invitationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Send, Mail, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';

const DAY_MS = 24 * 60 * 60 * 1000;
const EXPIRING_WINDOW_DAYS = 2;
const ACCEPTED_WINDOW_DAYS = 7;

export default function InvitationsSummary() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ pending: 0, expiringSoon: 0, acceptedThisWeek: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const res = await invitationsAPI.list(true);
      const now = Date.now();
      let pending = 0;
      let expiringSoon = 0;
      let acceptedThisWeek = 0;
      for (const inv of res.data || []) {
        if (inv.status === 'pending') {
          pending += 1;
          const expiresAt = Date.parse(inv.expires_at);
          if (!isNaN(expiresAt) && expiresAt - now <= EXPIRING_WINDOW_DAYS * DAY_MS) {
            expiringSoon += 1;
          }
        } else if (inv.status === 'accepted') {
          // No accepted_at in the public response shape; use expires_at
          // minus 7 days as an upper-bound proxy (invite was created ≤
          // 7 days before expires_at by construction, so acceptance
          // within the last 7 days means created_at is within 14 days).
          // We keep the signal simple with a conservative lower bound:
          // only count invites whose created_at is within the window.
          const createdAt = Date.parse(inv.created_at);
          if (!isNaN(createdAt) && now - createdAt <= ACCEPTED_WINDOW_DAYS * DAY_MS) {
            acceptedThisWeek += 1;
          }
        }
      }
      setStats({ pending, expiringSoon, acceptedThisWeek });
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') fetch();
  }, [user, fetch]);

  if (user?.role !== 'admin') return null;
  if (error) return null;

  const goToInvites = () => navigate('/settings?tab=invitations');

  return (
    <Card
      className="bg-[#2B2B2B] border-[#3D3D3D] hover:border-[#1ABC9C]/30 transition-colors shadow-light"
      data-testid="invitations-summary-widget"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-[#F2F2F2] text-base flex items-center gap-2">
            <Send className="w-4 h-4 text-[#1ABC9C]" />
            Team Invitations
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-[#1ABC9C] hover:bg-[#1ABC9C]/10 h-8"
            onClick={goToInvites}
            data-testid="invitations-summary-manage-btn"
          >
            Manage
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-16 flex items-center">
            <div className="w-5 h-5 border-2 border-[#1ABC9C]/30 border-t-[#1ABC9C] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <SummaryCell
              icon={Mail}
              label="Pending"
              value={stats.pending}
              tone="info"
              onClick={goToInvites}
              testId="invitations-summary-pending"
            />
            <SummaryCell
              icon={AlertCircle}
              label="Expiring soon"
              value={stats.expiringSoon}
              tone="warning"
              onClick={goToInvites}
              hint={`≤${EXPIRING_WINDOW_DAYS}d left`}
              testId="invitations-summary-expiring"
            />
            <SummaryCell
              icon={CheckCircle2}
              label="Joined this week"
              value={stats.acceptedThisWeek}
              tone="success"
              testId="invitations-summary-accepted"
            />
          </div>
        )}

        {!loading && stats.pending === 0 && stats.acceptedThisWeek === 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-md bg-[#1ABC9C]/5 border border-[#1ABC9C]/20 px-3 py-2">
            <p className="text-xs text-[#C7C7C7]">
              Invite your colleagues so Elara works for the whole team.
            </p>
            <Button
              size="sm"
              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 h-7"
              onClick={goToInvites}
              data-testid="invitations-summary-cta"
            >
              Send invite
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryCell({ icon: Icon, label, value, tone = 'info', onClick, hint, testId }) {
  const styles = {
    info: 'text-[#1ABC9C] bg-[#1ABC9C]/10 border-[#1ABC9C]/20',
    warning: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    success: 'text-[#1ABC9C] bg-[#1ABC9C]/10 border-[#1ABC9C]/20',
  }[tone];
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`text-left rounded-md border px-3 py-2 ${styles} ${
        clickable ? 'hover:scale-[1.02] cursor-pointer' : 'cursor-default'
      } transition-transform`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-1">
        <Icon className="w-3.5 h-3.5" />
        {hint && <span className="text-[10px] opacity-75">{hint}</span>}
      </div>
      <p className="text-2xl font-bold leading-none">{value}</p>
      <p className="text-[11px] opacity-75 mt-1">{label}</p>
    </button>
  );
}
